import Compressor from 'compressorjs';
import tenantSettingsConfig from '@/config/tenant-settings/tenant-settings.config';

export function deepMerge(target, source) {
	const output = { ...target };
	if (isObject(target) && isObject(source)) {
		Object.keys(source).forEach(key => {
			if (isObject(source[key])) {
				if (!(key in target)) {
					Object.assign(output, { [key]: source[key] });
				} else {
					output[key] = deepMerge(target[key], source[key]);
				}
			} else {
				Object.assign(output, { [key]: source[key] });
			}
		});
	}
	return output;
}

export function isObject(item) {
	return item && typeof item === 'object' && !Array.isArray(item);
}

export function mergeTenantSettings(tenantId, serverSettings = {}) {
	if (!tenantId) return serverSettings;

	const configEntry = tenantSettingsConfig.find(entry => entry.tenantId === tenantId);
	if (!configEntry || !configEntry.settings) return serverSettings;

	return deepMerge(serverSettings, configEntry.settings);
}

export function createQueryString(params) {
	return Object.keys(params)
		.map(k => {
			if (Array.isArray(params[k])) return params[k].map(x => encodeURIComponent(k) + '=' + encodeURIComponent(x)).join('&');
			return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
		})
		.join('&');
}

export function createUri(base, endPoint, { params = null, query = null } = {}) {
	if (params && typeof params === 'object') {
		for (let key in params) {
			endPoint = endPoint.replace(`:${key}`, params[key]);
		}
	}

	let url = `${base}/${endPoint}`;

	if (query && typeof query === 'object') {
		url += '?' + createQueryString(query);
	}

	return url;
}

export function createMap(value = [], idField = 'id') {
	if (!Array.isArray(value) || value.length === 0) return new Map();

	return value.reduce((map, v) => {
		if (typeof v !== 'object') return map;

		if (!map.has(value[idField])) {
			return map.set(value[idField], v);
		}
	}, new Map());
}

export function compressImage(file) {
	return new Promise((resolve, reject) => {
		new Compressor(file, {
			quality: 0.3,
			success(result) {
				resolve(result);
			},
			error(err) {
				reject(err);
			},
		});
	});
}

export function encodeImageBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader(file);

		reader.onloadend = () => {
			resolve(reader.result);
		};

		reader.onerror = reject;

		reader.readAsDataURL(file);
	});
}

export function getEnumValue(obj, name, invalid = '') {
	const v = Object.values(obj).find(x => x.name.toLowerCase() === name.toLowerCase());
	return v ? v.value : invalid;
}

export function getEnumName(obj, value, invalid = '') {
	const v = Object.values(obj).find(x => x.value === value);
	return v ? v.name : invalid;
}

export function getEnumProperty(obj, value, property = 'value', invalid = '') {
	const v = Object.values(obj).find(x => x.value === value);
	return v ? v[property] : invalid;
}

export function getDefaultShiftTimes(tenant) {
	const profile = tenant?.settings?.workflow?.professionalProfile;
	return {
		start: profile?.defaultShiftStartTime || '09:00',
		end: profile?.defaultShiftEndTime || '17:00',
	};
}

export function resolveContactHref(links = {}, brandUrl = '') {
	const number = String(links.whatsappNumber || '').replace(/\D/g, '');
	if (number) {
		const message = links.whatsappDefaultMessage || '';
		const text = message ? `?text=${encodeURIComponent(message)}` : '';
		return `https://wa.me/${number}${text}`;
	}
	if (links.contact) {
		return links.contact;
	}
	const base = (brandUrl || 'https://www.locumbase.com').replace(/\/$/, '');
	return `${base}/contact`;
}

const assetImageContext = require.context('@/assets/images', true, /\.(png|jpe?g|webp|svg)$/);

export function resolveAssetImage(relativePath, fallback) {
	if (!relativePath) {
		return fallback;
	}
	const normalized = String(relativePath).replace(/^\//, '');
	const key = `./${normalized}`;
	if (assetImageContext.keys().includes(key)) {
		return assetImageContext(key);
	}
	return fallback;
}
